/**
 * Shared OpenStreetMap Overpass API client used by the pub and park finders.
 *
 * Queries every public endpoint in parallel and takes the first good response,
 * aborting the rest. Each request has its own timeout.
 */

import { calculateDistance } from '../utils/geo';

export const OVERPASS_APIS = [
  'https://overpass.private.coffee/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass-api.de/api/interpreter',
];

const TIMEOUT = 10000; // 10 second timeout per endpoint

export class VenueLookupError extends Error {
  constructor(message, options) {
    super(message, options);
    this.name = 'VenueLookupError';
  }
}

/**
 * Build an Overpass QL query for node and way elements matching any of the
 * given tag filters within `radius` metres of a point.
 * @param {Array<[string, string]>} filters - e.g. [['amenity', 'pub']]
 */
export const buildAroundQuery = (filters, radius, latitude, longitude) => {
  const around = `(around:${radius},${latitude},${longitude})`;
  const clauses = filters
    .flatMap(([key, value]) => [
      `node["${key}"="${value}"]${around};`,
      `way["${key}"="${value}"]${around};`,
    ])
    .map((clause) => `      ${clause}`)
    .join('\n');

  return `
    [out:json][timeout:25];
    (
${clauses}
    );
    out center;
  `;
};

const queryEndpoint = async (endpoint, query, controller, timeout) => {
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      body: query,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Overpass API error from ${endpoint}: ${response.status}`);
    }

    const data = await response.json();

    if (!Array.isArray(data?.elements)) {
      throw new Error(`Overpass API returned no elements from ${endpoint}`);
    }

    return data;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Run an Overpass query against all endpoints in parallel.
 * @returns {Promise<{elements: Array}>} The first successful response body
 * @throws {VenueLookupError} When every endpoint fails or times out
 */
export const runOverpassQuery = async (query, { timeout = TIMEOUT } = {}) => {
  const controllers = OVERPASS_APIS.map(() => new AbortController());

  try {
    return await Promise.any(
      OVERPASS_APIS.map((endpoint, index) =>
        queryEndpoint(endpoint, query, controllers[index], timeout)
      )
    );
  } catch (error) {
    throw new VenueLookupError('All Overpass endpoints failed', { cause: error });
  } finally {
    controllers.forEach((controller) => controller.abort());
  }
};

/**
 * Turn Overpass elements into venues sorted by distance from the user.
 * Ways use their `center` point. Elements without tags or coordinates are dropped.
 * @param {Function} toVenue - Maps (element, tags) to venue-specific fields
 */
export const toVenuesByDistance = (elements, latitude, longitude, toVenue) =>
  elements
    .filter((element) => element.tags && (element.type === 'node' || element.type === 'way'))
    .map((element) => {
      const lat = element.lat ?? element.center?.lat;
      const lon = element.lon ?? element.center?.lon;

      if (lat == null || lon == null) return null;

      return {
        id: element.id,
        lat,
        lon,
        distance: calculateDistance(latitude, longitude, lat, lon),
        ...toVenue(element.tags),
      };
    })
    .filter((venue) => venue !== null)
    .sort((a, b) => a.distance - b.distance);
